// WAAP "API Protection → Vulnerability Detection" — detected-vulnerabilities list
// + managed rules (read / toggle / whitelist) + filter option & mapping lookups.
// Account-level (any of the account's domain ids resolves the customer).
//   GET        /api-protection/vulnerability/vulnerabilities?...
//   GET        /api-protection/vulnerability/rules
//   GET/PUT    /api-protection/vulnerability/rules/:ruleId
//   GET        /api-protection/vulnerability/api-asset-options
//   GET        /api-protection/vulnerability/api-app-mapping | api-asset-mapping
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS, type WaapArea } from './_shared';

export interface WaapVulnWhitelistEntry {
  key: string; // app
  operation: string; // equal
  value: string[];
}

export interface WaapVulnRuleConfig {
  level: string; // high | medium | low (Risk Level)
  sub_type: string;
  type: string; // Vulnerability Category
  suggestion: string;
  description: string;
  whitelist: WaapVulnWhitelistEntry[];
}

/** A managed vulnerability rule. type 1 = managed (fixed catalog). */
export interface WaapVulnRule {
  id: number;
  name: string;
  type: number;
  config: WaapVulnRuleConfig;
  created?: number;
  updated?: number;
  status: number; // 1 = on, 0 = off (table toggle)
  customer_id?: number;
}

/** One page of detected vulnerabilities. Item shape passed through raw (empty in
 *  the sample), resolved against the app/asset mappings for display. */
export interface WaapVulnerabilitiesPage {
  totalCount: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
  currentPageNumber: number;
  totalPageNumber: number;
  hasPre: boolean;
  hasNext: boolean;
  list: Record<string, unknown>[];
}

export interface WaapVulnQuery {
  page?: number;
  length?: number;
  beginTime?: number;
  endTime?: number;
  appId?: number;
  type?: string;
}

const rulesBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionVulnRules);
const rulesKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionVulnRules);
const vulnsBase = (domainId: string) => waapPath(domainId, WAAP_AREAS.apiProtectionVulnerabilities);
const vulnsKey = (domainId: string) => waapKeys.aspect(domainId, WAAP_AREAS.apiProtectionVulnerabilities);

/** List detected vulnerabilities (paginated, filtered). */
export function useWaapVulnerabilities(domainId: string | undefined, q: WaapVulnQuery = {}) {
  const { page = 1, length = 25, beginTime, endTime, appId, type } = q;
  return useQuery({
    queryKey: [...vulnsKey(domainId ?? ''), page, length, beginTime, endTime, appId, type],
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapVulnerabilitiesPage | null> => {
      try {
        const params = new URLSearchParams({ pageNumber: String(page), length: String(length) });
        if (beginTime) params.set('beginTime', String(beginTime));
        if (endTime) params.set('endTime', String(endTime));
        if (appId) params.set('appId', String(appId));
        if (type) params.set('type', type);
        const res = await serviceApiV3.get<WaapVulnerabilitiesPage>(`${vulnsBase(domainId as string)}?${params.toString()}`);
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** List all managed vulnerability rules. */
export function useWaapVulnRules(domainId: string | undefined) {
  return useQuery({
    queryKey: rulesKey(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapVulnRule[]> => {
      const res = await serviceApiV3.get<{ rules: WaapVulnRule[] }>(rulesBase(domainId as string));
      return res.data?.rules ?? [];
    },
  });
}

/** Read one managed vulnerability rule (editor prefill). */
export function useWaapVulnRule(domainId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: [...rulesKey(domainId ?? ''), ruleId],
    enabled: !!domainId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<WaapVulnRule | null> => {
      const res = await serviceApiV3.get<WaapVulnRule>(`${rulesBase(domainId as string)}/${ruleId}`);
      return res.data ?? null;
    },
  });
}

/** Edit a managed vulnerability rule (whitelist). Invalidates the list on success. */
export function useUpdateWaapVulnRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapVulnRule }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapVulnRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: rulesKey(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Toggle a rule's status in place — optimistic, NO refetch (keeps row order). */
export function useToggleWaapVulnRule(domainId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapVulnRule }) =>
      (await serviceApiV3.put(`${rulesBase(domainId)}/${id}`, body)).data,
    onMutate: async ({ id, body }: { id: number; body: WaapVulnRule }) => {
      await qc.cancelQueries({ queryKey: rulesKey(domainId) });
      const prev = qc.getQueryData<WaapVulnRule[]>(rulesKey(domainId));
      qc.setQueryData<WaapVulnRule[]>(rulesKey(domainId), (old) =>
        (old ?? []).map((r) => (r.id === id ? { ...r, status: body.status } : r)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(rulesKey(domainId), ctx.prev);
    },
  });
}

/** API-asset (v2) filter options. */
export function useWaapVulnAssetOptions(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.apiProtectionVulnAssetOptions),
    enabled: !!domainId,
    retry: false,
    queryFn: async () => {
      const res = await serviceApiV3.get<{ options: { label: string; value: string }[] }>(
        waapPath(domainId as string, WAAP_AREAS.apiProtectionVulnAssetOptions),
      );
      return res.data?.options ?? [];
    },
  });
}

function useVulnMapping(domainId: string | undefined, area: WaapArea) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', area),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<Record<string, string>> => {
      const res = await serviceApiV3.get<{ mapping: Record<string, string> }>(waapPath(domainId as string, area));
      return res.data?.mapping ?? {};
    },
  });
}

/** Application id→name mapping. */
export function useWaapVulnAppMapping(domainId: string | undefined) {
  return useVulnMapping(domainId, WAAP_AREAS.apiProtectionVulnAppMapping);
}

/** API-asset id→path mapping. */
export function useWaapVulnAssetMapping(domainId: string | undefined) {
  return useVulnMapping(domainId, WAAP_AREAS.apiProtectionVulnAssetMapping);
}
